import { useMemo } from 'react';
import { useAppSelector } from 'app/hooks';
import { ReferentialKey } from 'api/referential';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';

/**
 * Priority item from referential with color from database
 */
export interface PriorityItem {
  id: string;
  code: string;
  label: string;
  color: string;
}

/** Default color when none is provided */
const DEFAULT_COLOR = '#95A5A6';

/**
 * Hook to access note priorities from referential store
 */
export const useNotePriorities = () => {
  const prioritiesData = useAppSelector(
    selectReferentialDataByKey(ReferentialKey.NotePriority)
  );

  const priorities = useMemo<PriorityItem[]>(() => {
    if (!prioritiesData) return [];

    return prioritiesData.map((ref) => ({
      id: ref.id || '',
      code: ref.code || '',
      label: ref.label || '',
      // Use color from referential (database), fallback to default gray
      color: ref.color || DEFAULT_COLOR
    }));
  }, [prioritiesData]);

  /**
   * Get priority by ID
   */
  const getPriorityById = useMemo(() => {
    return (id?: string): PriorityItem | undefined => {
      if (!id) return undefined;
      return priorities.find((p) => p.id === id);
    };
  }, [priorities]);

  /**
   * Get priority by code
   */
  const getPriorityByCode = useMemo(() => {
    return (code?: string): PriorityItem | undefined => {
      if (!code) return undefined;
      return priorities.find((p) => p.code === code);
    };
  }, [priorities]);

  /**
   * Get dropdown options for priority filter (includes 'all' option)
   */
  const priorityFilterOptions = useMemo(() => {
    return [
      { value: 'all', label: 'Toutes' },
      ...priorities.map((p) => ({
        value: p.code,
        label: p.label
      }))
    ];
  }, [priorities]);

  /**
   * Get dropdown options for priority selection (no 'all' option)
   */
  const prioritySelectOptions = useMemo(() => {
    return priorities.map((p) => ({
      value: p.id,
      label: p.label,
      code: p.code,
      color: p.color
    }));
  }, [priorities]);

  return {
    priorities,
    getPriorityById,
    getPriorityByCode,
    priorityFilterOptions,
    prioritySelectOptions,
    isLoading: !prioritiesData
  };
};
